import EmptyLink from '@aytm/app/swidget/base/components/EmptyLink'
import useSurveyLocale from '@aytm/app/swidget/base/modules/locale/useSurveyLocale'
import useViewport from '@aytm/app/swidget/base/modules/viewport/useViewport.ts'
import commonStyle from '../../style.module.scss'
import style from './Footer.module.scss'
import fbIcon from '../assets/icons/socialFacebook.svg'
import igIcon from '../assets/icons/socialInstagram.svg'
import xIcon from '../assets/icons/socialX.svg'
import ptIcon from '../assets/icons/socialPinterest.svg'
import privacyChoiceIcon from '../assets/images/yourPrivacyChoice.png'

interface IFooterProps {
  // The filled cart pins its own summary bar into the bottom slot — the tab bar yields to it.
  cartBarShown: boolean
  scrollTop: () => void
  onHomeClick: () => void
  showHint: () => void
  closeSimulation: () => void
}

const COLUMNS = [
  {
    title: 'colMembership',
    links: [
      'linkJoin',
      'linkExclusiveOffers',
      'linkRegisterMembership',
      'linkRenewMembership',
      'linkApp',
      'linkForSuppliers'
    ]
  },
  {
    title: 'colShipping',
    links: ['linkTrackOrders', 'linkShippingFaqs', 'linkPickup', 'linkSameDayDelivery', 'linkReturnRefunds']
  },
  {
    title: 'colHelp',
    links: [
      'linkHelpCenter',
      'linkProductRecalls',
      'linkMyAccount',
      'linkAffiliates',
      'linkSparkDriver',
      'linkAdvertise'
    ]
  },
  {
    title: 'colResources',
    links: ['linkAboutUs', 'linkNewsroom', 'linkCareers', 'linkClubDirectory', 'linkTruckloads', 'linkExports']
  }
]

const SOCIAL = [
  { src: fbIcon, key: 'socialFacebook' },
  { src: igIcon, key: 'socialInstagram' },
  { src: xIcon, key: 'socialX' },
  { src: ptIcon, key: 'socialPinterest' }
]

const LEGAL_ROW_1 = [
  'legalSiteDirectory',
  'legalPrivacyNotice',
  'legalNoticeAtCollection',
  'legalAccessibility',
  'legalCaSupplyChain',
  'legalDisclosurePolicy',
  'legalHealthPrivacy'
]

const LEGAL_ROW_2 = ['legalSafetyDataSheet', 'legalAdChoices']

const SIGNUP_FIELDS = [
  { label: 'signupMobileLabel', placeholder: 'signupMobilePlaceholder', button: 'signupTexts' },
  { label: 'signupEmailLabel', placeholder: 'signupEmailPlaceholder', button: 'signupEmail' }
]

// The app's tab bar; only Home is live, the rest are inert like the desktop chrome.
const MOBILE_NAV = [
  { key: 'navScanGo', icon: 'ld-ScanAndGo' },
  { key: 'navReorder', icon: 'ld-Reorder' }
]

const Footer = ({ cartBarShown, scrollTop, onHomeClick, showHint, closeSimulation }: IFooterProps) => {
  const viewport = useViewport()
  const { t } = useSurveyLocale()

  if (viewport.mobile) {
    if (cartBarShown) return null
    return (
      <footer className={style.footerMobile} data-testid="SW-EC-Footer">
        <button type="button" className={`${style.navItem} ${style.navItemActive}`} onClick={onHomeClick}>
          <i className={`ld-Home ${style.navIcon}`} />
          <span className={style.navLabel}>{t('questions', 'ec', 'samsclub', 'navHome')}</span>
        </button>
        {MOBILE_NAV.map(({ key, icon }) => (
          <EmptyLink key={key} color="#0a6cff">
            <span className={style.navItem}>
              <i className={`${icon} ${style.navIcon}`} />
              <span className={style.navLabel}>{t('questions', 'ec', 'samsclub', key)}</span>
            </span>
          </EmptyLink>
        ))}
        <div className={`${commonStyle.IconAytmPlace} ${style.helperIcon}`}>
          <i className="icon-aytmfd-help" onClick={showHint} />
        </div>
        <div className={`${commonStyle.IconAytmPlace} ${style.helperIcon}`}>
          <i className="icon-aytmfd-exit" onClick={closeSimulation} />
        </div>
      </footer>
    )
  }

  return (
    <footer data-testid="SW-EC-Footer">
      <div className={style.top}>
        {COLUMNS.map(col => (
          <div className={style.column} key={col.title}>
            <div className={style.columnTitle}>{t('questions', 'ec', 'samsclub', col.title)}</div>
            {col.links.map(link => (
              <EmptyLink key={link} color="#0a6cff">
                <span className={style.columnLink}>{t('questions', 'ec', 'samsclub', link)}</span>
              </EmptyLink>
            ))}
          </div>
        ))}
        <div className={style.signup}>
          <div className={style.signupTitle}>{t('questions', 'ec', 'samsclub', 'signupTitle')}</div>
          {SIGNUP_FIELDS.map(({ label, placeholder, button }) => (
            <div className={style.signupRow} key={button}>
              <div className={style.signupField}>
                <span className={style.signupLabel}>{t('questions', 'ec', 'samsclub', label)}</span>
                <EmptyLink color="#0a6cff">
                  <span className={style.signupInput}>{t('questions', 'ec', 'samsclub', placeholder)}</span>
                </EmptyLink>
              </div>
              <EmptyLink color="#0a6cff">
                <span className={style.signupButton}>{t('questions', 'ec', 'samsclub', button)}</span>
              </EmptyLink>
            </div>
          ))}
        </div>
      </div>

      <div className={style.bottom}>
        <div className={style.social}>
          {SOCIAL.map(({ src, key }) => (
            <EmptyLink key={key} color="#0a6cff">
              <img src={src} alt={t('questions', 'ec', 'samsclub', key)} className={style.socialIcon} />
            </EmptyLink>
          ))}
        </div>
        <div className={style.legalRow}>
          {LEGAL_ROW_1.map(link => (
            <EmptyLink key={link} color="#0a6cff">
              <span className={style.legalLink}>{t('questions', 'ec', 'samsclub', link)}</span>
            </EmptyLink>
          ))}
        </div>
        <div className={style.legalRow}>
          {LEGAL_ROW_2.map(link => (
            <EmptyLink key={link} color="#0a6cff">
              <span className={style.legalLink}>{t('questions', 'ec', 'samsclub', link)}</span>
            </EmptyLink>
          ))}
          <EmptyLink color="#0a6cff">
            <span className={style.legalLink}>
              <img src={privacyChoiceIcon} alt="" role="presentation" className={style.privacyChoiceIcon} />
              {t('questions', 'ec', 'samsclub', 'legalPrivacyChoices')}
            </span>
          </EmptyLink>
        </div>
        <div className={style.bottomLine}>
          <span className={style.copyrightGroup}>
            <EmptyLink color="#0a6cff">
              <span className={style.legalLink}>{t('questions', 'ec', 'samsclub', 'legalTerms')}</span>
            </EmptyLink>
            <span className={style.copyright}>{t('questions', 'ec', 'samsclub', 'copyright')}</span>
          </span>
          <button type="button" className={style.backToTop} onClick={scrollTop}>
            <i className={`pi pi-caret-up ${style.backToTopIcon}`} />
            {t('questions', 'ec', 'samsclub', 'backToTop')}
          </button>
        </div>
      </div>
    </footer>
  )
}

export default Footer
